import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useDrawerEscape } from '../../../lib/hooks/useDrawerEscape';

interface DrawerShellProps {
  /** Kicker in alto (gradiente Flusia), es. "Accettazione contratto". */
  kicker: string;
  onClose: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
  /** Il gate dati cliente si apre anche sopra modali (offerta a z 20000). */
  zClass?: string;
  /** Livello per l'Escape condiviso: va allineato allo z-index di `zClass`. */
  escapeLayer?: number;
}

/**
 * Chrome del drawer laterale destro per i flussi contratto (accettazione e
 * gate dati cliente): stesso linguaggio del drawer "Nuova anagrafica"
 * (overlay, pannello 520px, kicker gradiente, footer fisso). Esc passa dal
 * gestore condiviso (useDrawerEscape): chiude solo il pannello piu' in alto,
 * non anche il modale sottostante.
 */
export function DrawerShell({ kicker, onClose, footer, children, zClass = 'z-[9999]', escapeLayer = 9999 }: DrawerShellProps) {
  useDrawerEscape(true, onClose, escapeLayer);

  // Portal sul body: renderizzato in-tree, un antenato con transform/z-index
  // intrappola lo z del drawer e il modale Invia Proposta (portal #modal-root)
  // finiva SOPRA il gate. A livello body lo z-[21000] vince davvero.
  return createPortal(
    <div className={`fixed inset-0 ${zClass} overflow-hidden`} role="dialog" aria-label={kicker}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-[520px] flex-col bg-white dark:bg-[#202020] shadow-xl border-l border-[rgba(55,53,47,0.16)] dark:border-white/10">
        <div className="flex items-center justify-between px-5 pt-4 pb-1">
          <span className="text-[11px] font-semibold uppercase tracking-widest bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent">
            {kicker}
          </span>
          <button
            type="button" onClick={onClose} aria-label="Chiudi"
            className="rounded p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {/* bg esplicito: senza, il contenuto scrollato può trasparire sotto il footer */}
        <div className="flex items-center gap-2 border-t border-[rgba(55,53,47,0.09)] bg-white dark:border-white/10 dark:bg-[#202020] px-4 py-3">
          {footer}
        </div>
      </div>
    </div>,
    document.body
  );
}
